/**
 * OpenAI Service for Review Replies and Business Descriptions
 * Uses OpenAI API to generate intelligent, personalized responses
 */
import logger from '../utils/logger';
import api from './api';

class OpenAIReviewService {
  constructor() {
    // Switch to backend proxy; keep truthy for existing checks
    this.apiKey = 'backend-proxy';
    this.apiVersion = 'v1beta';
    this.baseUrl = `https://generativelanguage.googleapis.com/${this.apiVersion}/models`;
    this.defaultModel = 'gemini-1.5-flash';
    this.modelDiscoveryOrder = [
      'models/gemini-2.5-flash',
      'models/gemini-1.5-flash-latest',
      'models/gemini-1.5-flash',
      'models/gemini-1.5-pro-latest',
      'models/gemini-1.5-pro',
      'models/text-bison-001'
    ];
    this.currentModel = this.defaultModel;
    this.apiUrl = `${this.baseUrl}/${this.currentModel}:generateContent`;
    this.modelInitialized = false;
    this.modelInitPromise = null;

    if (this.apiKey) {
      // Kick off model discovery in the background
      this.modelInitPromise = this.initializeModel().catch(error => {
        logger.error('⚠️ [OpenAI] Model discovery failed, using default model:', error.message);
        this.modelInitialized = true;
      });
    } else {
      logger.warn('⚠️ [OpenAI] API key not configured. AI features will use fallback templates.');
    }
  }

  async ensureModelInitialized() {
    if (!this.apiKey) return;
    if (this.modelInitialized) return;
    if (!this.modelInitPromise) {
      this.modelInitPromise = this.initializeModel();
    }
    await this.modelInitPromise;
  }

  async initializeModel() {
    try {
      const models = await this.listAvailableModels(false);
      const generationModels = models.filter(model =>
        Array.isArray(model.supportedGenerationMethods) &&
        model.supportedGenerationMethods.includes('generateContent')
      );

      const preferredModel = this.modelDiscoveryOrder.find(orderName =>
        generationModels.some(model => model.name === orderName)
      );

      const chosenModel = preferredModel ||
        (generationModels.length > 0 ? generationModels[0].name : null);

      if (chosenModel) {
        this.currentModel = chosenModel.split('/').pop();
        this.apiUrl = `${this.baseUrl}/${this.currentModel}:generateContent`;
        logger.log(`✅ [OpenAI] Using model: ${this.currentModel}`);
      } else {
        // No models discovered from backend — fall back to default model configuration
        this.currentModel = this.defaultModel;
        this.apiUrl = `${this.baseUrl}/${this.currentModel}:generateContent`;
        logger.log('ℹ️ [OpenAI] No remote model discovered — using default model configuration');
      }
    } catch (error) {
      logger.error('⚠️ [OpenAI] Failed to initialize model:', error);
    } finally {
      this.modelInitialized = true;
    }
  }

  /**
   * List all available OpenAI models (for debugging)
   */
  async listAvailableModels(logResults = true) {
    try {
      const resp = await api.get('/customer/ai/models');
      const models = resp?.data?.models || resp?.models || [];
      if (logResults) {
        logger.log('📋 Available OpenAI Models:');
        models.forEach(m => {
          const supportsGenerate = m.supportedGenerationMethods?.includes('generateContent');
          logger.log(`- ${m.name}${supportsGenerate ? ' ✅ (supports generateContent)' : ''}`);
        });
      }
      return models;
    } catch (error) {
      logger.error('Error listing models:', error);
      return [];
    }
  }

  /**
   * Generate AI-powered reply for a customer review
   * @param {Object} review - Review object with text, rating, author
   * @param {Object} businessInfo - Business information
   * @param {string} tone - Reply tone: 'professional', 'friendly', 'grateful', 'apologetic'
   * @returns {Promise<string>} - Generated reply text
   */
  async generateReviewReply(review, businessInfo = {}, tone = 'professional') {
    try {
      const authorName = review?.author || review?.reviewer?.displayName || 'Customer';
      const reviewText = review?.text || review?.comment || '';
      
      logger.log('🤖 [OpenAI] Generating personalized reply for:', authorName);
      if (reviewText) {
        logger.log('📝 [Review Content]:', reviewText.substring(0, 50) + '...');
      }
      
      const prompt = this.buildReviewReplyPrompt(review, businessInfo, tone);
      
      if (!this.apiKey) {
        logger.warn('⚠️ [OpenAI] API key not found! Using template fallback.');
        logger.warn('💡 Add OPENAI_API_KEY to backend .env file for AI-powered replies');
        const templateReply = this.generateTemplateReply(review, businessInfo, tone);
        return String(templateReply || 'Thank you for your review!');
      }
      
      if (this.apiKey) {
        try {
          logger.log('🚀 [OpenAI] Calling OpenAI API for personalized reply...');
          const aiResponse = await this.callGeminiAPI(prompt);
          if (aiResponse && typeof aiResponse === 'string' && aiResponse.trim()) {
            logger.log('✅ [OpenAI] AI-generated PERSONALIZED reply successful!');
            logger.log('📄 [AI Reply Preview]:', aiResponse.substring(0, 80) + '...');
            return String(aiResponse.trim());
          }
        } catch (error) {
          logger.error('❌ [OpenAI] API call failed:', error.message);
          logger.warn('🔄 [OpenAI] Falling back to template reply');
        }
      }
      
      // Fallback: Use template-based reply
      logger.log('📋 [OpenAI] Using template-based reply (not personalized)');
      const templateReply = this.generateTemplateReply(review, businessInfo, tone);
      return String(templateReply || 'Thank you for your review!');
      
    } catch (error) {
      logger.error('[OpenAI] Error generating review reply:', error);
      const basicReply = this.generateBasicReply(review);
      return String(basicReply || 'Thank you for your feedback!');
    }
  }

  /**
   * Generate AI-powered business description
   * @param {Object} businessData - Business information (name, category, services, etc.)
   * @returns {Promise<string>} - Generated business description
   */
  async generateBusinessDescription(businessData) {
    try {
      logger.log('[OpenAI] Generating business description...');
      
      const prompt = this.buildBusinessDescriptionPrompt(businessData);
      
      if (this.apiKey) {
        try {
          const aiResponse = await this.callGeminiAPI(prompt);
          if (aiResponse && aiResponse.trim()) {
            logger.log('[OpenAI] AI-generated description successful');
            return aiResponse.trim();
          }
        } catch (error) {
          logger.warn('[OpenAI] API call failed, using fallback:', error);
        }
      }
      
      // Fallback: Use template-based description
      return this.generateTemplateDescription(businessData);
      
    } catch (error) {
      logger.error('[OpenAI] Error generating business description:', error);
      return this.generateBasicDescription(businessData);
    }
  }

  /**
   * Generate bulk AI replies for multiple reviews
   * @param {Array} reviews - Array of review objects
   * @param {Object} businessInfo - Business information
   * @param {Object} options - Options for bulk generation
   * @returns {Promise<Array>} - Array of generated replies with review IDs
   */
  async generateBulkReplies(reviews, businessInfo = {}, options = {}) {
    try {
      logger.log(`[OpenAI] Generating bulk replies for ${reviews.length} reviews...`);
      
      const {
        ratingFilter = [1, 2, 3, 4, 5], // Which ratings to reply to
        tone = 'professional',
        delayMs = 1000 // Delay between API calls to avoid rate limits
      } = options;
      
      const results = [];
      
      for (const review of reviews) {
        // Skip if rating not in filter
        if (!ratingFilter.includes(review.rating)) {
          continue;
        }
        
        // Generate reply
        const reply = await this.generateReviewReply(review, businessInfo, tone);
        
        results.push({
          reviewId: review.id,
          reviewText: review.text,
          reviewRating: review.rating,
          reviewAuthor: review.author,
          generatedReply: reply,
          timestamp: new Date().toISOString()
        });
        
        // Delay to avoid rate limits
        if (delayMs > 0) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
      
      logger.log(`[OpenAI] Generated ${results.length} bulk replies`);
      return results;
      
    } catch (error) {
      logger.error('[OpenAI] Error generating bulk replies:', error);
      return [];
    }
  }

  /**
   * Build prompt for review reply generation
   */
  buildReviewReplyPrompt(review, businessInfo, tone) {
    const { name = 'our business', category = 'business' } = businessInfo;
    const toneGuide = this.getToneGuide(tone, review.rating);
    
    return `You are a professional customer service representative for ${name}, a ${category}.

Generate a ${tone} reply to this SPECIFIC customer review:

Review Rating: ${review.rating}/5 stars
Review Author: ${review.author}
Review Text: "${review.text}"

${toneGuide}

IMPORTANT - Your reply MUST:
- Directly reference specific points mentioned in the review text above
- Address the customer by their first name (${review.author.split(' ')[0]})
- Acknowledge the specific experiences, services, or products they mentioned
- Be personalized and unique to THIS review - do not use generic phrases
- Keep reply between 50-100 words
- Be genuine and authentic, not robotic
- ${review.rating >= 4 ? 'Thank them for the SPECIFIC positive feedback they mentioned and encourage them to visit again' : 'Acknowledge their SPECIFIC concerns professionally and show commitment to improvement'}
- ${review.rating <= 2 ? 'Offer to resolve the issue offline' : ''}
- Mention the business name (${name}) naturally
- Use proper grammar and punctuation
- End with a simple warm sentence like "We look forward to seeing you again!" or "Thank you for choosing us!"

DO NOT include:
- Any signature lines like "Best regards" or "Sincerely"
- Any placeholder text like "[Your Name]" or "[Business Name]"
- Any job titles like "Customer Service Representative"
- Any formal closing signatures

Generate ONLY the reply text without any signature or closing name:`;
  }

  /**
   * Build prompt for business description generation
   */
  buildBusinessDescriptionPrompt(businessData) {
    const {
      name = 'Business',
      category = 'business',
      services = [],
      specialties = [],
      location = '',
      yearEstablished = '',
      uniqueSellingPoints = []
    } = businessData;
    
    return `You are a professional business copywriter. Generate a compelling, SEO-optimized business description for Google My Business profile.

Business Information:
- Name: ${name}
- Category: ${category}
- Location: ${location || 'Not specified'}
- Established: ${yearEstablished || 'Not specified'}
${services.length > 0 ? `- Services: ${services.join(', ')}` : ''}
${specialties.length > 0 ? `- Specialties: ${specialties.join(', ')}` : ''}
${uniqueSellingPoints.length > 0 ? `- Unique Selling Points: ${uniqueSellingPoints.join(', ')}` : ''}

Requirements:
- Length: 150-300 words
- Write in third person
- Include relevant keywords naturally for SEO
- Highlight what makes this business unique
- Mention location if provided
- Professional yet approachable tone
- Include a call-to-action at the end
- Focus on benefits to customers
- Use engaging, persuasive language

Generate only the business description text, without any labels or additional commentary:`;
  }

  /**
   * Get tone guidance based on rating
   */
  getToneGuide(tone, rating) {
    if (rating >= 4) {
      const guides = {
        professional: 'Use a professional and appreciative tone. Be warm but maintain business standards.',
        friendly: 'Use a friendly, conversational tone. Show genuine excitement about their positive experience.',
        grateful: 'Express heartfelt gratitude. Let them know how much their feedback means to your business.',
      };
      return guides[tone] || guides.professional;
    } else if (rating === 3) {
      return 'Use a balanced, understanding tone. Acknowledge both positive aspects and areas for improvement. Show commitment to delivering better experiences.';
    } else {
      const guides = {
        professional: 'Use an apologetic yet professional tone. Take responsibility and show commitment to resolution.',
        apologetic: 'Express sincere apologies. Show empathy and urgency in addressing their concerns.',
      };
      return guides[tone] || guides.professional;
    }
  }

  /**
   * Call Gemini AI API
   */
  async callGeminiAPI(prompt) {
    const resp = await api.post('/customer/ai/generate', {
      prompt,
      model: this.currentModel,
      generationConfig: {
        temperature: 0.8,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 1024,
      }
    });
    return resp?.data?.text || resp?.text || '';
  }

  /**
   * Generate template-based reply (fallback when AI not available)
   */
  generateTemplateReply(review, businessInfo, tone) {
    try {
      const { name = 'our business' } = businessInfo || {};
      const authorName = review?.author || review?.reviewer?.displayName || 'Valued Customer';
      const firstName = authorName.split(' ')[0] || authorName;
      const rating = review?.rating || 5;
      
      if (rating >= 4) {
        // Positive review templates
        const templates = [
          `Thank you so much, ${firstName}! We're delighted to hear you had a great experience at ${name}. Your kind words mean the world to us, and we can't wait to serve you again soon!`,
          `${firstName}, thank you for taking the time to share your wonderful feedback! We're thrilled that you enjoyed your experience with ${name}. Looking forward to welcoming you back!`,
          `We truly appreciate your positive review, ${firstName}! It's fantastic to know that ${name} met your expectations. We look forward to seeing you again soon!`
        ];
        return templates[Math.floor(Math.random() * templates.length)];
        
      } else if (rating === 3) {
        // Neutral review template
        return `Thank you for your feedback, ${firstName}. We appreciate you taking the time to share your experience at ${name}. We're always working to improve, and your input helps us do that. We'd love the opportunity to provide you with an even better experience next time!`;
        
      } else {
        // Negative review template
        return `Thank you for bringing this to our attention, ${firstName}. We sincerely apologize that your experience at ${name} didn't meet your expectations. This is not the level of service we strive for. We would love the opportunity to make this right. Please contact us directly so we can address your concerns properly. Your satisfaction is our priority.`;
      }
    } catch (error) {
      logger.error('[OpenAI] Error in generateTemplateReply:', error);
      return 'Thank you for your review! We appreciate your feedback and look forward to serving you again.';
    }
  }

  /**
   * Generate template-based description (fallback when AI not available)
   */
  generateTemplateDescription(businessData) {
    const { name, category, location, services = [] } = businessData;
    
    let description = `${name} is a trusted ${category}`;
    if (location) description += ` located in ${location}`;
    description += `. `;
    
    if (services.length > 0) {
      description += `We specialize in ${services.slice(0, 3).join(', ')}`;
      if (services.length > 3) description += ` and more`;
      description += `. `;
    }
    
    description += `Our dedicated team is committed to providing exceptional service and quality products to all our customers. Visit us today to experience the difference!`;
    
    return description;
  }

  /**
   * Generate basic reply (last resort fallback)
   */
  generateBasicReply(review) {
    const firstName = review.author.split(' ')[0];
    if (review.rating >= 4) {
      return `Thank you for your wonderful review, ${firstName}! We appreciate your feedback and look forward to serving you again!`;
    } else {
      return `Thank you for your feedback, ${firstName}. We take all reviews seriously and would love the opportunity to address your concerns. Please contact us directly.`;
    }
  }

  /**
   * Generate basic description (last resort fallback)
   */
  generateBasicDescription(businessData) {
    const { name, category } = businessData;
    return `${name} is your trusted ${category}. We are committed to providing excellent service and quality products to our customers. Visit us today!`;
  }

  /**
   * Check if Gemini AI is configured
   */
  isConfigured() {
    return !!this.apiKey;
  }

  /**
   * Get configuration status
   */
  getStatus() {
    return {
      configured: this.isConfigured(),
      apiKey: this.apiKey ? `${this.apiKey.substring(0, 10)}...` : 'Not configured',
      endpoint: this.apiUrl
    };
  }
}

// Export singleton instance
export default new OpenAIReviewService();
